/**
 * Local Storage Service for SWORN Build Planner
 * Provides localStorage fallback when Firebase is unavailable
 */
import { BuildData } from '../types';

const STORAGE_KEY = 'sworn_builds';
const USER_ID_KEY = 'sworn_user_id';

let cachedBuilds: BuildData[] | null = null;

interface CustomGlobal extends Window {
    __app_id?: string;
}

export const getLocalUserId = (): string => {
  let userId = localStorage.getItem(USER_ID_KEY);
  if (!userId) {
    userId = 'local_' + crypto.randomUUID();
    localStorage.setItem(USER_ID_KEY, userId);
  }
  return userId;
};

export const getAllBuilds = (): BuildData[] => {
  if (cachedBuilds) return [...cachedBuilds];

  try {
    const data = localStorage.getItem(STORAGE_KEY);
    const builds = data ? (JSON.parse(data) as BuildData[]) : [];
    cachedBuilds = builds.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return [...cachedBuilds];
  } catch (error) {
    console.error('Error reading builds from localStorage:', error);
    // Clear potentially corrupted data
    localStorage.removeItem(STORAGE_KEY);
    cachedBuilds = [];
    return [];
  }
};

const persistBuilds = (builds: BuildData[]) => {
    cachedBuilds = builds;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(builds));
    } catch (error) {
        console.error('Error saving to localStorage:', error);
    }
};

export const saveBuild = (build: Omit<BuildData, 'id' | 'createdAt' | 'updatedAt'>): BuildData => {
  const builds = getAllBuilds(); // Ensures we always read the latest, sorted list (from cache if avail)
  const newBuild: BuildData = {
    ...build,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  // Add to the beginning for chronological order (newest first)
  const newBuilds = [newBuild, ...builds];
  persistBuilds(newBuilds);
  return newBuild;
};

export const updateBuild = (id: string, updates: Partial<Omit<BuildData, 'id' | 'createdAt'>>): BuildData | null => {
  let builds = getAllBuilds();
  const index = builds.findIndex(b => b.id === id);
  if (index === -1) {
      console.warn(`Build with ID ${id} not found for update.`);
      return null;
  }

  const updatedBuild = {
      ...builds[index],
      ...updates,
      updatedAt: new Date().toISOString()
  };

  // Create new array to avoid mutating cache directly if reference was somehow shared improperly derived
  const newBuilds = [...builds];
  newBuilds[index] = updatedBuild;

  newBuilds.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  
  persistBuilds(newBuilds);

  return updatedBuild; 
};


export const deleteBuild = (id: string): boolean => {
  const builds = getAllBuilds();
  const initialLength = builds.length;
  const filtered = builds.filter(b => b.id !== id);
  if (filtered.length === initialLength) {
    console.warn(`Build with ID ${id} not found for deletion.`);
    return false;
  }

  persistBuilds(filtered);
  return true;
};

export const getBuildById = (id: string): BuildData | null => {
  const build = getAllBuilds().find(b => b.id === id);
  return build || null;
};
